import React, { memo, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { type SanityPromoSettings } from '@data/sanity/queries/types/site'

import CustomLink from '@components/link'
import { AnimatePresence, motion } from 'framer-motion'
import { debounce } from 'throttle-debounce'
import { promoBarAnimation } from '@lib/animate'

type PromoBarProps = SanityPromoSettings

const CYCLE_DELAY = 3000
const CYCLE_SCREEN_WIDTH_BREAKPOINT = 768
const CYCLE_ANIMATION_DURATION = 0.3

const PromoBar = memo(({ enabled, messages, link, display }: PromoBarProps) => {
  const router = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)

  const [shouldCycle, setShouldCycle] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const type = display?.trim()

  // Check that promo bar is enabled, type and joinedMessages are given and the page matches, if type is homepage
  const isPromoVisible = !!(
    enabled &&
    type &&
    messages &&
    (type !== 'home' || router.asPath === '/')
  )

  // If screen is < MD or if messages don't fit container, cycle through messages
  useEffect(() => {
    if (!isPromoVisible) {
      return
    }

    function checkDimensions() {
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const messagesWidth = messagesRef.current?.scrollWidth ?? 0
      const isMdOrBelow = window.innerWidth <= CYCLE_SCREEN_WIDTH_BREAKPOINT

      const adjustedContainerWidth = containerWidth - 100 // -100px for padding

      setShouldCycle(isMdOrBelow || messagesWidth > adjustedContainerWidth)
    }

    const debouncedCheck = debounce(500, checkDimensions)
    checkDimensions()

    window.addEventListener('resize', debouncedCheck)
    return () => window.removeEventListener('resize', debouncedCheck)
  }, [messages, isPromoVisible])

  useEffect(() => {
    if (!shouldCycle || !messages || messages.length < 2) {
      return
    }
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % messages.length)
    }, CYCLE_DELAY)
    return () => clearInterval(interval)
  }, [messages, shouldCycle])

  if (!isPromoVisible) {
    return null
  }

  const joinedMessages = messages.join(' | ')

  return (
    <div
      ref={containerRef}
      className="relative z-60 bg-pageBG text-pageText text-center whitespace-nowrap overflow-hidden xs:px-page-padding"
    >
      <div ref={messagesRef} className="inline-flex px-8 py-2 text-sm">
        {shouldCycle && (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              variants={promoBarAnimation}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{
                duration: CYCLE_ANIMATION_DURATION,
              }}
              className="block"
            >
              {!!link && (
                <CustomLink
                  link={{
                    page: link,
                  }}
                  className="block no-underline"
                >
                  {messages[currentIndex]}
                </CustomLink>
              )}
              {!link && <>{messages[currentIndex]}</>}
            </motion.div>
          </AnimatePresence>
        )}

        {!shouldCycle && (
          <>
            {!!link && (
              <CustomLink
                link={{
                  page: link,
                }}
                className="block no-underline"
              >
                {joinedMessages}
              </CustomLink>
            )}
            {!link && <>{joinedMessages}</>}
          </>
        )}
      </div>
    </div>
  )
})

PromoBar.displayName = 'PromoBar'

export default PromoBar
